import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage"; // localStorage
import type { IAuthGateway } from "@domain/ports/IAuthGateway";
import type { IRestApiGateway } from "@domain/ports/IRestApiGateway";
import type { IProfileGateway } from "@domain/ports/IProfileGateway";
import type { IPairingGateway } from "@domain/ports/IPairingGateway";
import type { IPublicTrophyRoomGateway } from "@domain/ports/IPublicTrophyRoomGateway";
import type { ITrophyRoomGateway } from "@domain/ports/ITrophyRoomGateway";
import type { IPublicScanGateway } from "@domain/ports/IPublicScanGateway";
import type { IWeb3AuthGateway } from "@domain/ports/IWeb3AuthGateway";
import type { IAnalyticsGateway } from "@domain/ports/IAnalyticsGateway";
import { CognitoAuthGateway } from "@/adapters/gateways/CognitoAuthGateway";
import { RestApiGateway } from "@/adapters/gateways/RestApiGateway";
import { HttpProfileGateway } from "@/adapters/gateways/HttpProfileGateway";
import { HttpPairingGateway } from "@/adapters/gateways/HttpPairingGateway";
import { HttpPublicTrophyRoomGateway } from "@/adapters/gateways/HttpPublicTrophyRoomGateway";
import { HttpTrophyRoomGateway } from "@/adapters/gateways/HttpTrophyRoomGateway";
import { HttpPublicScanGateway } from "@/adapters/gateways/HttpPublicScanGateway";
import { PostHogAnalyticsGateway } from "@/adapters/gateways/PostHogAnalyticsGateway";
import authReducer, {
  setSessionExpired,
  logoutUser,
  refreshToken as refreshTokenThunk,
} from "./slices/authSlice";
import signupReducer from "./slices/signupSlice";
import passwordResetReducer from "./slices/passwordResetSlice";
import profileReducer from "./slices/profileSlice";
import pairingReducer from "./slices/pairingSlice";
import publicTrophyRoomReducer from "./slices/publicTrophyRoomSlice";
import trophyRoomReducer from "./slices/trophyRoomSlice";
import publicScanReducer from "./slices/publicScanSlice";
import embeddedWalletReducer from "./slices/embeddedWalletSlice";
import onboardingReducer from "./slices/onboardingSlice";
import analyticsReducer from "./slices/analyticsSlice";
import {
  posthogMetricsMiddleware,
  initPosthogMiddleware,
} from "@/utils/metrics/posthog.middleware";

/**
 * Redux Persist Configuration - Auth
 *
 * Configures persistence for auth state to survive OAuth redirects and page reloads.
 * Only persists critical authentication data, not transient UI state.
 */
const authPersistConfig = {
  key: "auth",
  version: 1, // For future state migrations
  storage,
  whitelist: ["token", "user", "returnUrl", "rememberMe", "isAuthenticated"],
  // Exclude transient UI state: loading flags, errors
};

// Wrap auth reducer with persistence
const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);

/**
 * Redux Persist Configuration - Onboarding
 *
 * Configures persistence for onboarding state to track which
 * onboarding experiences the user has completed.
 * All state is persisted since there is no transient UI state.
 */
const onboardingPersistConfig = {
  key: "onboarding",
  version: 1, // For future state migrations
  storage,
  whitelist: ["hasSeenEntranceAnimation", "hasSeenFirstLoginWelcome", "hasGivenGdprConsent"],
};

// Wrap onboarding reducer with persistence
const persistedOnboardingReducer = persistReducer(
  onboardingPersistConfig,
  onboardingReducer
);

/**
 * Redux Persist Configuration - Analytics
 *
 * Configures persistence for analytics consent state.
 * Only persists consentGiven to respect user choice across sessions.
 * Transient state (initialized, identified, userId) is not persisted.
 */
const analyticsPersistConfig = {
  key: "analytics",
  version: 1, // For future state migrations
  storage,
  whitelist: ["consentGiven"],
  // Exclude transient state: initialized, identified, userId
};

// Wrap analytics reducer with persistence
const persistedAnalyticsReducer = persistReducer(
  analyticsPersistConfig,
  analyticsReducer
);

/**
 * Store Extra Dependencies Interface
 *
 * Defines external dependencies injected into Redux thunks via middleware.
 * This enables dependency injection for better testability.
 */
export interface StoreExtraDependencies {
  authGateway: IAuthGateway;
  apiGateway: IRestApiGateway;
  profileGateway: IProfileGateway;
  pairingGateway: IPairingGateway;
  publicTrophyRoomGateway: IPublicTrophyRoomGateway;
  trophyRoomGateway: ITrophyRoomGateway;
  publicScanGateway: IPublicScanGateway;
  /**
   * Web3Auth Gateway for embedded wallet operations
   *
   * Optional for now - will be injected from React context in Phase 9.
   * The embeddedWalletSlice uses its own EmbeddedWalletThunkExtra type
   * since the gateway instance comes from useWeb3Auth hook, not store config.
   */
  web3AuthGateway?: IWeb3AuthGateway;
  /**
   * Analytics Gateway for PostHog integration
   *
   * Story 4.1: PostHog Frontend Analytics
   * Used by posthogMetricsMiddleware to capture events.
   */
  analyticsGateway: IAnalyticsGateway;
}

/**
 * Create Redux store with dependency injection
 *
 * @param dependencies - External dependencies to inject into thunks
 * @returns Configured Redux store
 */
export const createStore = (dependencies: StoreExtraDependencies) => {
  // Initialize PostHog middleware with analytics gateway
  // Must be done before store creation so middleware can capture events
  initPosthogMiddleware(dependencies.analyticsGateway);

  return configureStore({
    reducer: {
      auth: persistedAuthReducer, // Use persisted auth reducer
      signup: signupReducer,
      passwordReset: passwordResetReducer,
      profile: profileReducer,
      pairing: pairingReducer,
      publicTrophyRoom: publicTrophyRoomReducer,
      trophyRoom: trophyRoomReducer,
      publicScan: publicScanReducer,
      embeddedWallet: embeddedWalletReducer,
      onboarding: persistedOnboardingReducer, // Use persisted onboarding reducer
      analytics: persistedAnalyticsReducer, // Use persisted analytics reducer (Story 4.1)
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // Ignore all Redux Persist action types to prevent false warnings
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
        thunk: {
          extraArgument: dependencies,
        },
      }).prepend(posthogMetricsMiddleware.middleware),
  });
};

// Infer the type of the store
export type AppStore = ReturnType<typeof createStore>;

// Infer RootState and AppDispatch types from the store itself
export type RootState = ReturnType<AppStore["getState"]>;
export type AppDispatch = AppStore["dispatch"];

// Type for async thunks with extra argument
export type AppThunk<ReturnType = void> = (
  dispatch: AppDispatch,
  getState: () => RootState,
  extraArgument: StoreExtraDependencies
) => ReturnType;

/**
 * Production store instance
 *
 * Created with real gateway implementations.
 * For testing, use createStore() with FakeAuthGateway and FakeProfileGateway.
 */
const ownerApiBaseUrl =
  import.meta.env.VITE_OWNER_API_BASE_URL || "http://localhost:3000";
const viewerApiBaseUrl = import.meta.env.VITE_VIEWER_API_BASE_URL;
const nfcBaseUrl =
  import.meta.env.VITE_NFC_BASE_URL || "https://app.anyw3ar.com";

// Helper to get auth token from store
let currentStore: AppStore | null = null;
const getAuthToken = () => {
  if (!currentStore) return null;
  return currentStore.getState().auth.token;
};

/**
 * Refresh token using Redux thunk
 * Story 4.9: Used by HTTP interceptor for 401 retry
 */
const refreshAuthToken = async (): Promise<string | null> => {
  if (!currentStore) return null;
  try {
    const result = await currentStore.dispatch(refreshTokenThunk()).unwrap();
    return result;
  } catch {
    return null;
  }
};

/**
 * Handle session expiration
 * Story 4.9: Called by HTTP interceptor when token refresh fails
 */
const handleSessionExpired = (): void => {
  if (!currentStore) return;
  currentStore.dispatch(setSessionExpired(true));
  currentStore.dispatch(logoutUser());
};

export const store = createStore({
  authGateway: new CognitoAuthGateway(),
  apiGateway: new RestApiGateway(),
  profileGateway: new HttpProfileGateway(
    ownerApiBaseUrl,
    getAuthToken,
    refreshAuthToken,
    handleSessionExpired
  ),
  pairingGateway: new HttpPairingGateway(
    ownerApiBaseUrl,
    getAuthToken,
    refreshAuthToken,
    handleSessionExpired
  ),
  publicTrophyRoomGateway: new HttpPublicTrophyRoomGateway(viewerApiBaseUrl),
  trophyRoomGateway: new HttpTrophyRoomGateway(ownerApiBaseUrl, getAuthToken),
  publicScanGateway: new HttpPublicScanGateway(viewerApiBaseUrl, nfcBaseUrl),
  analyticsGateway: new PostHogAnalyticsGateway(), // Story 4.1: PostHog Analytics
});

// Store reference for token access
currentStore = store;

/**
 * Redux Persist Persistor
 *
 * Manages persistence lifecycle for the Redux store.
 * Used by PersistGate to delay app rendering until state is rehydrated.
 */
export const persistor = persistStore(store);
